import { useEffect, useState, memo } from "react";

import { fetchFundlist } from "../utils";

import { COLOR } from "../config";

import { Application, Environment, Fund, Locale } from "../types";

import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";

import { Stack, TextField, Autocomplete, Typography, Chip, CircularProgress } from "@mui/material";

import type { AutocompleteRenderGetTagProps, AutocompleteRenderOptionState } from "@mui/material"; // eslint-disable-line no-duplicate-imports

import Clear from "@mui/icons-material/Clear";

type AutocompleteComponentProps = {
  setFundlist: (fundlist: Fund[]) => void;
  disabled: boolean;
  options: Fund[];
  label: string;
  dropdownPlaceholder: string;
  multiple: boolean;
  disableClearable: boolean;
  setValue: (fund: unknown) => void;
  disableSelect: boolean;
  selectedOptions: Fund[];
  locale: Locale;
  environment: Environment;
  application: Application;
};

const AutocompleteComponent = memo((props: AutocompleteComponentProps) => {
  const {
    setFundlist,
    disabled,
    options,
    label,
    dropdownPlaceholder,
    multiple,
    disableClearable,
    setValue,
    disableSelect,
    selectedOptions,
    locale,
    environment,
    application,
  } = props;

  const [open, setOpen] = useState(false);
  const loading = open && options.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) return;

    (async () => {
      const fundlist = await fetchFundlist(locale, environment, application);
      if (active) setFundlist(fundlist);
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  const getOptionLabel = (option: Fund) => {
    const fund = options.filter((_) => _?.id === option?.id)[0];
    if (fund === null) {
      return "";
    } else {
      return `${fund.label}: ${fund.isins}`;
    }
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Fund,
    { inputValue }: AutocompleteRenderOptionState,
  ) => {
    const fund = options.filter((_) => _?.id === option?.id)[0];
    if (fund === null) return <li {...props}></li>;

    const { label, isins } = fund;
    const labelParts = parse(label, match(label, inputValue));
    const isinsParts = parse(isins, match(isins, inputValue));

    return (
      <li {...props}>
        <Stack>
          <Typography variant="body2" fontWeight={500}>
            {labelParts.map((part, index) => (
              <span
                key={index}
                style={{
                  backgroundColor: part.highlight ? COLOR.blueTransparent : "transparent",
                }}
              >
                {part.text}
              </span>
            ))}
          </Typography>
          <Typography variant="caption" gutterBottom>
            {isinsParts.map((part, index) => (
              <span
                key={index}
                style={{
                  backgroundColor: part.highlight ? COLOR.blueTransparent : "transparent",
                }}
              >
                {part.text}
              </span>
            ))}
          </Typography>
        </Stack>
      </li>
    );
  };

  const renderTags = (value: Fund[], getTagProps: AutocompleteRenderGetTagProps) => {
    return value.map((option, index) => (
      <Chip // eslint-disable-line react/jsx-key
        sx={{
          backgroundColor: COLOR.blue3,
          color: "#303030",
          borderRadius: "4px !important",
        }}
        label={getOptionLabel(option)}
        deleteIcon={
          <Clear
            sx={{
              color: `#303030 !important`,
              fontSize: "20px !important",
            }}
          />
        }
        {...getTagProps({ index })}
      />
    ));
  };

  const getOptionDisabled = (option: Fund) => {
    if (disableSelect || selectedOptions.includes(option)) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <Autocomplete
      openOnFocus
      filterSelectedOptions
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      disabled={disabled ? true : false}
      multiple={multiple ? true : false}
      disableClearable={disableClearable ? true : false}
      options={options}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={dropdownPlaceholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderTags={renderTags}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      onChange={(e, val) => {
        setValue(val);
      }}
      getOptionDisabled={getOptionDisabled}
    />
  );
});

export default AutocompleteComponent;
